<script setup lang="ts">
import { Monitor, Moon, Sun } from "lucide-vue-next"

import AppTooltip from "@/components/AppTooltip.vue"
import Breadcrumbs from "@/components/Breadcrumbs.vue"
import NotificationBell from "@/components/NotificationBell.vue"
import { Button } from "@/components/ui/button"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { useAppearance } from "@/composables/useAppearance"
import {
  notificationPath,
  notificationsPath,
  pollNotificationsPath,
  readAllNotificationsPath,
  readNotificationPath,
} from "@/routes"
import type { BreadcrumbItemType } from "@/types"

withDefaults(
  defineProps<{
    breadcrumbs?: BreadcrumbItemType[]
  }>(),
  {
    breadcrumbs: () => [],
  },
)

const { appearance, updateAppearance } = useAppearance()

const modes = [
  { value: "light" as const, icon: Sun, label: "Light mode" },
  { value: "dark" as const, icon: Moon, label: "Dark mode" },
  { value: "system" as const, icon: Monitor, label: "System theme" },
]

function cycleAppearance() {
  const idx = modes.findIndex((m) => m.value === appearance.value)
  const next = modes[(idx + 1) % modes.length]
  updateAppearance(next.value)
}

const currentMode = () => modes.find((m) => m.value === appearance.value) ?? modes[2]
</script>

<template>
  <header
    class="border-sidebar-border/70 flex h-16 shrink-0 items-center gap-2 border-b px-6 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-4"
  >
    <div class="flex flex-1 items-center gap-2">
      <SidebarTrigger class="-ml-1" />
      <template v-if="breadcrumbs && breadcrumbs.length > 0">
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
      </template>
    </div>

    <div class="flex items-center gap-1">
      <AppTooltip :content="currentMode().label" side="bottom" :delay-duration="0">
        <Button variant="ghost" size="icon" class="size-9" @click="cycleAppearance">
          <component :is="currentMode().icon" class="size-4" />
          <span class="sr-only">{{ currentMode().label }}</span>
        </Button>
      </AppTooltip>

      <NotificationBell
        :notifications-path="notificationsPath()"
        :read-path="(id) => readNotificationPath(id)"
        :read-all-path="readAllNotificationsPath()"
        :poll-path="pollNotificationsPath()"
        :delete-notification-path="(id) => notificationPath(id)"
      />
    </div>
  </header>
</template>
